// doctor-frontend/src/clinical/visitGuidelineRegistry.js
// Versioned, reviewable clinical-guidance registry.
// Guideline/source updates are staged separately from React components. A Visit
// records the rule-pack id/version active at creation so historical Visits remain
// reproducible. No new source should change patient-care logic until reviewed,
// tested and explicitly activated.

import { getSectionModulePlan } from './visitSectionModules';

export const DEFAULT_RULE_PACK = Object.freeze({
  id: 'cmfm-clinical-foundation',
  version: '2026.09-foundation.2',
  status: 'active',
  activatedAt: null,
  sourceStatus: 'programme-specific reviewed source packs not yet activated',
});

export const RULE_PACK_UPDATE_STATES = Object.freeze([
  'draft',
  'staged',
  'reviewed',
  'active',
  'retired',
]);

// Registry entries can later be loaded from an admin-managed backend rather
// than bundled source. Keep the shape stable so national/international source
// updates can be compared, reviewed, activated and rolled back without changing
// the clinical screens.
export const CLINICAL_RULE_PACK_REGISTRY = Object.freeze([
  {
    id: 'cmfm-clinical-foundation',
    version: '2026.09-foundation',
    status: 'retired',
    jurisdiction: 'India',
    scope: 'CMFM clinical-workflow foundation',
    sourceDocuments: [],
    supersedes: null,
    reviewedAt: null,
    sourceStatus: 'programme-specific reviewed source packs not yet activated',
    changeSummary: 'Initial pathway/module provenance foundation.',
  },
  {
    ...DEFAULT_RULE_PACK,
    jurisdiction: 'India',
    scope: 'CMFM clinical-workflow foundation',
    sourceDocuments: [],
    supersedes: 'cmfm-clinical-foundation@2026.09-foundation',
    reviewedAt: null,
    changeSummary: 'Adds section-specific module priorities, guidance presentation and source-update lifecycle hooks.',
  },
]);

export const GUIDANCE_SECTION_LABELS = Object.freeze({
  history: 'History guidance',
  examination: 'Examination guidance',
  investigations: 'Investigation guidance',
  prescription: 'Treatment & prescription guidance',
});

export function guidanceContextForVisit(visit = {}, section = 'history') {
  const modules = Array.isArray(visit?.activeClinicalModules) ? visit.activeClinicalModules : [];
  const complaints = Array.isArray(visit?.chiefComplaints)
    ? visit.chiefComplaints.map((item) => item?.complaint || item?.label).filter(Boolean)
    : [];
  const plan = getSectionModulePlan({ modules, section });
  const requestedId = visit?.clinicalRulePackId || DEFAULT_RULE_PACK.id;
  const requestedVersion = visit?.clinicalRulePackVersion || DEFAULT_RULE_PACK.version;
  const pack = CLINICAL_RULE_PACK_REGISTRY.find((item) => (
    item.id === requestedId && item.version === requestedVersion
  )) || DEFAULT_RULE_PACK;

  return {
    section,
    sectionLabel: GUIDANCE_SECTION_LABELS[section] || 'Clinical guidance',
    pathway: visit?.clinicalPathway || visit?.clinicalFocus || '',
    serviceArea: visit?.serviceArea || visit?.visitCategory || '',
    triagePriority: visit?.triagePriority || '',
    complaints,
    modules,
    prompts: plan.prompts,
    examSystems: plan.examSystems,
    investigationBlocks: plan.investigationBlocks,
    medicineCategories: plan.medicineCategories,
    rulePackId: requestedId,
    rulePackVersion: requestedVersion,
    rulePackStatus: pack.status || DEFAULT_RULE_PACK.status,
    sourceStatus: pack.sourceStatus || DEFAULT_RULE_PACK.sourceStatus,
  };
}
